import { Controller } from "stimulus";

// Site-wide session cookie: theater survives Turbo navigations and is shared across tabs, but dies
// with the browser. Must match CookieHelper::THEATER_MODE - nothing enforces the two stay in sync.
const THEATER_COOKIE = "theater_mode";
// One-shot hand-over across a full reload of /live (its own reloads, or F5). Read and deleted by
// LivePagesController#take_live_theater_cookie, whose delete path must match this one.
const VISIT_COOKIE = "live_theater";
const VISIT_COOKIE_PATH = "/live";
const VISIT_COOKIE_MAX_AGE_SECONDS = 10;

// Typing keys go to these; a range slider (the players' seek and volume bars) keeps focus after a drag
// but types nothing, so the hotkeys still work there.
const TEXT_ENTRY = "input:not([type=range]):not([type=checkbox]):not([type=radio]):not([type=button]), textarea, select, [contenteditable]";

// Set on the document's first connect only: later bodies come from Turbo renders, not a page load.
let documentLoad = true;

function secureSuffix() {
  return window.location.protocol === "https:" ? "; Secure" : "";
}

// The page's one owner of theater state, on <body>. The players' buttons only ask to toggle; the
// storage value decides how the state outlives a render:
// - cookie (application layout): the site-wide theater_mode cookie, server-rendered on the next page.
// - visit (/live): never touches theater_mode. Kept across same-page Turbo refreshes and, through
//   VISIT_COOKIE, across full reloads, so every new visit starts in the normal layout.
export default class extends Controller {
  static targets = ["button", "player"];
  static values = { storage: String };

  initialize() {
    this.boundKeydown = (event) => this.handleKeydown(event);
    this.boundKeydownCapture = (event) => this.noteOverlay(event);
    this.boundPageShow = (event) => this.handlePageShow(event);
    this.boundBeforeUnload = () => this.handleBeforeUnload();
    this.boundVisit = (event) => this.handleVisit(event);
    this.boundBeforeRender = (event) => this.handleBeforeRender(event);
  }

  connect() {
    // The native shell plays video natively and CSS-hides the web player.
    if (document.querySelector("[data-hotwire-native]")) return;

    this.active = true;
    this.currentPath = window.location.pathname;
    this.destinationPath = null;

    // window, not document: runs after every document-level handler, so one that consumed Esc
    // (the chat's moderator confirm) has already called preventDefault.
    window.addEventListener("keydown", this.boundKeydown);
    window.addEventListener("keydown", this.boundKeydownCapture, true);
    window.addEventListener("pageshow", this.boundPageShow);

    const pageLoad = documentLoad;
    documentLoad = false;

    if (this.visitStorage) {
      document.addEventListener("turbo:visit", this.boundVisit);
      document.addEventListener("turbo:before-render", this.boundBeforeRender);
      // A refreshed body can arrive carrying the state with no player left on it (the stream ended).
      // And only a reload carries theater into a full page load: VISIT_COOKIE is written on every
      // unload, so a tab reopened or a back navigation inside its lifetime still starts normal.
      if (!this.playerPresent || (pageLoad && !this.reloaded)) this.exit();
      this.syncUnloadListener();
    } else {
      this.restoreFromCookie();
    }
    this.syncButtons();
  }

  disconnect() {
    if (!this.active) return;

    this.active = false;
    cancelAnimationFrame(this.recheckFrame);
    window.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("keydown", this.boundKeydownCapture, true);
    window.removeEventListener("pageshow", this.boundPageShow);
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
    document.removeEventListener("turbo:visit", this.boundVisit);
    document.removeEventListener("turbo:before-render", this.boundBeforeRender);
  }

  toggle(event) {
    this.set(!this.on);
  }

  exit() {
    this.set(false);
  }

  // Plyr injects its button only once it boots, and rebuilds its controls, so this runs late.
  buttonTargetConnected(button) {
    button.setAttribute("aria-pressed", String(this.on));
  }

  // On the next frame, so a stage frame swap (old player out, new player in) doesn't exit.
  playerTargetDisconnected() {
    if (!this.active || !this.visitStorage) return;

    cancelAnimationFrame(this.recheckFrame);
    this.recheckFrame = requestAnimationFrame(() => {
      if (this.active && !this.playerPresent) this.exit();
    });
  }

  // --- Private ---

  get on() {
    return document.body.hasAttribute("data-theater");
  }

  get visitStorage() {
    return this.storageValue === "visit";
  }

  // Only auto-exit uses this. Never visibility: below md the button is display:none, and Plyr
  // rebuilds its controls while booting, so a visibility check would wrongly exit on a refresh.
  get playerPresent() {
    return this.playerTargets.some(player => this.usable(player));
  }

  // Not every one of them calls preventDefault when it closes on Escape (the players' menus and dialogs).
  get overlayOpen() {
    if (document.querySelector("dialog[open], [data-theater-target='player'] [aria-expanded='true']")) return true;

    try {
      return document.querySelector(":popover-open") !== null;
    } catch {
      return false;
    }
  }

  usable(element) {
    return !element.closest("[inert]");
  }

  set(enabled) {
    document.body.toggleAttribute("data-theater", enabled);
    this.syncButtons();
    if (this.visitStorage) {
      this.syncUnloadListener();
    } else {
      this.writeCookie(enabled);
    }
  }

  syncButtons() {
    this.buttonTargets.forEach(button => button.setAttribute("aria-pressed", String(this.on)));
  }

  // "t" toggles and Escape exits, like the player's other single-key hotkeys: not while typing,
  // not with modifiers, and not when something else already handled the key.
  handleKeydown(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;

    const target = event.target;
    if (target?.isContentEditable || target?.closest?.(TEXT_ENTRY)) return;

    if (event.key === "Escape") {
      // Fullscreen, and any dialog or menu that was open when the key went down, own Escape.
      if (!this.on || document.fullscreenElement || this.overlayOpenAtKeydown) return;

      event.preventDefault();
      this.exit();
    } else if (event.key?.toLowerCase() === "t") {
      // The button is hidden below its breakpoint, so the hotkey must not act there either.
      if (!this.buttonTargets.some(button => this.usable(button) && !button.hidden && button.offsetParent !== null)) return;

      event.preventDefault();
      this.toggle();
    }
  }

  // Capture phase, before Plyr's own menu listener closes its menu (without preventDefault) and the
  // open state is gone by the time handleKeydown runs.
  noteOverlay(event) {
    if (event.key === "Escape") this.overlayOpenAtKeydown = this.overlayOpen;
  }

  // --- Cookie storage ---

  // Turbo restoration visits and bfcache restores reuse a <body> without re-requesting, so the
  // server-rendered state can be stale - re-derive it whenever a page with a player shows. Never on
  // a page without one, or the cookie would collapse that page's chrome with no way back.
  restoreFromCookie() {
    if (!this.playerPresent) return;

    document.body.toggleAttribute("data-theater", document.cookie.split(";").some(c => c.trim() === `${THEATER_COOKIE}=1`));
  }

  handlePageShow(event) {
    if (!event.persisted) return;

    if (!this.visitStorage) this.restoreFromCookie();
    this.syncButtons();
  }

  writeCookie(enabled) {
    document.cookie = `${THEATER_COOKIE}=${enabled ? "1" : "0"}; path=/; SameSite=Lax${secureSuffix()}`;
  }

  // --- Visit storage ---

  handleVisit(event) {
    this.destinationPath = new URL(event.detail.url, window.location.href).pathname;
  }

  // Turbo replaces the whole <body>; a refresh or same-page render carries the state over, any
  // other page drops it.
  handleBeforeRender(event) {
    if (!this.on || this.destinationPath !== this.currentPath) return;

    event.detail.newBody?.setAttribute("data-theater", "");
  }

  // beforeunload, not pagehide: pagehide fires only once the next page's response is in, after its
  // request went out without the cookie.
  handleBeforeUnload() {
    document.cookie = `${VISIT_COOKIE}=1; path=${VISIT_COOKIE_PATH}; max-age=${VISIT_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secureSuffix()}`;
  }

  // Only while theater is on: in Firefox a beforeunload listener keeps the page out of the bfcache.
  syncUnloadListener() {
    if (this.on && this.active) {
      window.addEventListener("beforeunload", this.boundBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", this.boundBeforeUnload);
    }
  }

  get reloaded() {
    return performance.getEntriesByType?.("navigation")?.[0]?.type === "reload";
  }
}
